import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, X } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import { CatalogApp } from './catalogTypes';
import { getCatalogIcon } from './catalogIcons';
import { logCatalog } from './catalogLogger';

interface CatalogAppPopupProps {
  app: CatalogApp;
  onClose: () => void;
}

const CatalogAppPopup: React.FC<CatalogAppPopupProps> = ({ app, onClose }) => {
  const { theme } = useTheme();
  const isLight = theme === 'white';
  const Icon = getCatalogIcon(app.icon);
  const [iframeFailed, setIframeFailed] = useState(false);

  const openLive = () => {
    logCatalog('app', 'open_live_from_popup', { id: app.id, url: app.url });
    window.open(app.url, '_blank', 'noopener,noreferrer');
  };

  return createPortal(
    <div className="fixed inset-0 z-[170] bg-black/70 backdrop-blur-sm p-3 md:p-4 flex">
      <div
        className={`flex flex-col w-full h-full rounded-xl overflow-hidden border shadow-2xl ${
          isLight ? 'bg-white border-neutral-200' : 'bg-neutral-950 border-neutral-800'
        }`}
      >
        <div
          className={`flex items-center justify-between gap-3 px-4 py-3 border-b shrink-0 ${
            isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900/80'
          }`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg shrink-0 ${isLight ? 'bg-neutral-100 border border-neutral-200' : 'bg-neutral-800 border border-neutral-700'}`}>
              <Icon size={18} className={isLight ? 'text-neutral-700' : 'text-white'} />
            </div>
            <div className="min-w-0">
              <h2 className={`text-sm font-semibold truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{app.name}</h2>
              <p className={`text-xs truncate ${isLight ? 'text-neutral-500' : 'text-gray-400'}`}>{app.dns}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openLive}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
            >
              <ExternalLink size={12} />
              Open in new tab
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded-lg transition-colors ${
                isLight ? 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-200' : 'text-gray-400 hover:text-white hover:bg-neutral-800'
              }`}
              aria-label="Close popup"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 relative bg-neutral-950">
          {iframeFailed ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
              <p className="text-sm text-gray-300 mb-2">This URL cannot be embedded in a popup (the site blocks iframes).</p>
              <p className={`text-xs mb-4 ${isLight ? 'text-neutral-500' : 'text-gray-500'}`}>{app.url}</p>
              <button
                type="button"
                onClick={openLive}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <ExternalLink size={14} />
                Open Live
              </button>
            </div>
          ) : (
            <iframe
              title={app.name}
              src={app.url}
              className="absolute inset-0 w-full h-full border-0 bg-white"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              referrerPolicy="no-referrer"
              onError={() => {
                logCatalog('warning', 'iframe_load_failed', { id: app.id, url: app.url });
                setIframeFailed(true);
              }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CatalogAppPopup;
